/*
 * Copyright (c) 2020. World Wide Generation Ltd
 */

import { SurveyUserRoles, UserRoles } from '@constants/user';
import { UserMin } from '@g17eco/types/user';
import { ValueList } from '../types/valueList';
import { SurveyConfig } from '../types/surveyConfig';
import { ValueHistory } from '../types/universalTrackerValue';
import { ScopeGroups } from '../types/surveyCommon';
import config from '../config';
import { OktaStorage } from '../services/OktaStorage';
import { BaseApi } from '../services/g17eco/BaseApi';

const G17Client = new BaseApi({ baseURL: config.apiUrl }, OktaStorage);
export const apiClient = G17Client;

type Options = Record<string, unknown>;

export const getValueList = async (id: string, options?: Options): Promise<ValueList> => {
  return G17Client.get(`value-list/${id}`, options)
    .then(response => response.data.data)
}

export const getSurveyConfig = async (id: string, options?: Options): Promise<SurveyConfig> => {
  return G17Client.get(`surveys/${id}/config`, options)
    .then(response => response.data.data)
}

export const updateSurveyScope = async (id: string, scopeGroups?: RequestScope[]): Promise<void> => {
  return G17Client.patch(`/surveys/${id}/scope`, { scopeGroups })
    .then(response => response.data.data)
};

export const removeSurveyScope = async (id: string, scopeGroups?: RequestScope[]): Promise<void> => {
  return G17Client.delete(`/surveys/${id}/scope`, { data: { scopeGroups } })
    .then(response => response.data.data)
};

export const updateSurveyConfig = async (id: string, data: unknown, options?: Options): Promise<SurveyConfig> => {
  return G17Client.patch(`surveys/${id}/config`, data, options)
    .then(response => response.data.data)
}

interface SurveyUsersResponse {
  users: (UserMin & { roles: UserRoles[] })[];
}

export const getSurveyUsers = async (id: string, options?: Options): Promise<SurveyUsersResponse> => {
  return G17Client.get(`surveys/${id}/users`, options)
    .then(response => response.data.data)
}

export type ScopeType = string | ScopeGroups;

export interface RequestScope {
  code: string;
  scopeType: ScopeType;
  scopeTags?: string[];
}

export interface BaseDelegationScopeRequest {
  id: string;
  role: SurveyUserRoles;
  scopeGroups?: RequestScope[];
}

export interface DelegationScopeUser extends BaseDelegationScopeRequest {
  userId: string;
  onboardingEmail?: string;
}
type delegationFn = (request: DelegationScopeUser) => Promise<any>;
type delegationRoleFn = (id: string, role: SurveyUserRoles, userId: string, scopeTags: string[], onboardingEmail?: string) => Promise<any>;

export const addDelegationScopeUser: delegationFn = async ({ id, scopeGroups, role, userId, onboardingEmail }) => {
  const url = onboardingEmail ? `surveys/${id}/users/${role}/${onboardingEmail}/onboard` : `surveys/${id}/users/${role}/${userId}`;
  return G17Client.patch(url, { scopeGroups })
    .then(response => response.data.data)
}

export const removeDelegationScopeUser: delegationFn = async ({ id, role, userId, scopeGroups, onboardingEmail }) => {
  const url = onboardingEmail ? `surveys/${id}/users/${role}/${onboardingEmail}/onboard` : `surveys/${id}/users/${role}/${userId}`;
  return G17Client.delete(url, { data: { scopeGroups } })
    .then(response => response.data.data)
}

export const addSurveyRoleUser: delegationRoleFn = async (id, role, userId, scopeTags, onboardingEmail) => {
  const url = onboardingEmail ? `surveys/${id}/users/${role}/${onboardingEmail}/onboard` : `surveys/${id}/users/${role}/${userId}`;
  return G17Client.patch(url, { data: scopeTags })
    .then(response => response.data.data)
}

export const removeSurveyRoleUser: delegationRoleFn = async (id, role, userId, scopeTags, onboardingEmail) => {
  const url = onboardingEmail ? `surveys/${id}/users/${role}/${onboardingEmail}/onboard` : `surveys/${id}/users/${role}/${userId}`;
  return G17Client.delete(url, { data: { data: scopeTags } })
    .then(response => response.data.data)
}

export const processSelfOnboarding = async (form: unknown) => {
  return G17Client.post('/onboarding/self', form)
    .then(response => response.data.data)
};

export const setCurrentHistoryData = async (id: string, data: ValueHistory) => {
  return G17Client.patch(`/universal-tracker-values/${id}/history/update`, { data })
    .then(response => response.data.data);
}
